import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  HStack,
  VStack,
  Icon,
  Badge,
  Select,
  IconButton,
  Tooltip,
  TableContainer,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import {
  FaEye,
  FaFileAlt,
  FaChevronLeft,
  FaChevronRight,
  FaCalendarAlt,
  FaUsers,
  FaSort,
  FaSortUp,
  FaSortDown,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const MotionTr = motion(Tr);

const GlassMorphismCard = ({ children, ...props }) => {
  const cardBg = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)",
  );
  const borderColor = useColorModeValue(
    "rgba(255, 255, 255, 0.2)",
    "rgba(255, 255, 255, 0.1)",
  );

  return (
    <Box
      bg={cardBg}
      backdropFilter="blur(20px)"
      borderRadius="20px"
      border="1px solid"
      borderColor={borderColor}
      boxShadow="xl"
      position="relative"
      overflow="hidden"
      _before={{
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: "20px",
        padding: "1px",
        background: `linear-gradient(135deg, ${borderColor}, transparent)`,
        mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
        maskComposite: "subtract",
        pointerEvents: "none",
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

export default function PayrunsTable({ payoutsData = [] }) {
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedYear, setSelectedYear] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const textColor = useColorModeValue("gray.700", "white");
  const subTextColor = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const headerBg = useColorModeValue("gray.50", "gray.800");
  const cardBg = useColorModeValue("white", "gray.800");

  // All months
  const allMonths = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Get unique years from data
  const availableYears = useMemo(() => {
    const years = [...new Set(payoutsData.map((p) => p.year))];
    return years.sort((a, b) => b - a);
  }, [payoutsData]);

  // Filter and sort payruns
  const filteredPayruns = useMemo(() => {
    let filtered = [...payoutsData];

    // Apply month filter
    if (selectedMonth !== "all") {
      filtered = filtered.filter((payrun) => payrun.month === selectedMonth);
    }

    // Apply year filter
    if (selectedYear !== "all") {
      filtered = filtered.filter(
        (payrun) => payrun.year?.toString() === selectedYear,
      );
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (
          sortConfig.key === "total_net_amount" ||
          sortConfig.key === "total_gross_amount"
        ) {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        }

        if (sortConfig.key === "payout_count") {
          aValue = (a.payouts || []).length;
          bValue = (b.payouts || []).length;
        }

        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [payoutsData, selectedMonth, selectedYear, sortConfig]);

  // Calculate totals for each payrun
  const payrunsWithTotals = useMemo(() => {
    return filteredPayruns.map((payrun) => {
      const payouts = payrun.payouts || [];
      const totals = payouts.reduce(
        (acc, payout) => ({
          gross: acc.gross + (payout.GrossAmount || 0),
          net: acc.net + (payout.NetAmount || 0),
          count: acc.count + 1,
        }),
        { gross: 0, net: 0, count: 0 },
      );
      return {
        ...payrun,
        total_gross_amount: totals.gross,
        total_net_amount: totals.net,
        payout_count: totals.count,
      };
    });
  }, [filteredPayruns]);

  // Pagination
  const totalPages = Math.ceil(payrunsWithTotals.length / pageSize);
  const paginatedPayruns = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return payrunsWithTotals.slice(startIndex, startIndex + pageSize);
  }, [payrunsWithTotals, currentPage, pageSize]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return FaSort;
    return sortConfig.direction === "asc" ? FaSortUp : FaSortDown;
  };

  const handleViewPayrun = (payrun) => {
    navigate(`/admin/payouts/payrun/${payrun.payrun_id}`);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    return payrunsWithTotals.reduce(
      (acc, payrun) => ({
        totalPayruns: acc.totalPayruns + 1,
        totalPayouts: acc.totalPayouts + payrun.payout_count,
        totalNet: acc.totalNet + payrun.total_net_amount,
      }),
      { totalPayruns: 0, totalPayouts: 0, totalNet: 0 },
    );
  }, [payrunsWithTotals]);

  return (
    <GlassMorphismCard p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header with Filters */}
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "stretch", md: "center" }}
          gap={4}
        >
          <HStack spacing={4} flex={1}>
            <Select
              value={selectedMonth}
              onChange={(e) => {
                setSelectedMonth(e.target.value);
                setCurrentPage(1);
              }}
              bg={cardBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="xl"
              maxW={{ base: "100%", md: "200px" }}
              _focus={{
                borderColor: "brand.500",
                boxShadow: "0 0 0 1px #23A979",
              }}
            >
              <option value="all">All Months</option>
              {allMonths.map((month) => (
                <option key={month} value={month}>
                  {month}
                </option>
              ))}
            </Select>

            <Select
              value={selectedYear}
              onChange={(e) => {
                setSelectedYear(e.target.value);
                setCurrentPage(1);
              }}
              bg={cardBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="xl"
              maxW={{ base: "100%", md: "150px" }}
              _focus={{
                borderColor: "brand.500",
                boxShadow: "0 0 0 1px #23A979",
              }}
            >
              <option value="all">All Years</option>
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </Select>
          </HStack>

          {/* Summary Stats */}
          <HStack spacing={6} display={{ base: "none", lg: "flex" }}>
            <VStack spacing={0} align="end">
              <Text fontSize="xs" color={subTextColor} fontWeight="medium">
                Total Payruns
              </Text>
              <Text fontSize="lg" fontWeight="bold" color="brand.500">
                {summaryStats.totalPayruns}
              </Text>
            </VStack>
            <VStack spacing={0} align="end">
              <Text fontSize="xs" color={subTextColor} fontWeight="medium">
                Total Payouts
              </Text>
              <Text fontSize="lg" fontWeight="bold" color="blue.500">
                {summaryStats.totalPayouts}
              </Text>
            </VStack>
            <VStack spacing={0} align="end">
              <Text fontSize="xs" color={subTextColor} fontWeight="medium">
                Total Net Amount
              </Text>
              <Text fontSize="lg" fontWeight="bold" color="green.500">
                ₹
                {summaryStats.totalNet.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                })}
              </Text>
            </VStack>
          </HStack>
        </Flex>

        {/* Table */}
        {payrunsWithTotals.length > 0 ? (
          <>
            <TableContainer
              borderRadius="xl"
              border="1px solid"
              borderColor={borderColor}
            >
              <Table variant="simple">
                <Thead bg={headerBg}>
                  <Tr>
                    <Th
                      border="none"
                      color={subTextColor}
                      fontSize="xs"
                      fontWeight="bold"
                      letterSpacing="wider"
                      textTransform="uppercase"
                      py={4}
                      cursor="pointer"
                      onClick={() => handleSort("month")}
                      _hover={{ color: "brand.500" }}
                    >
                      <HStack spacing={1}>
                        <Text>Period</Text>
                        <Icon as={getSortIcon("month")} boxSize={3} />
                      </HStack>
                    </Th>
                    <Th
                      border="none"
                      color={subTextColor}
                      fontSize="xs"
                      fontWeight="bold"
                      letterSpacing="wider"
                      textTransform="uppercase"
                      py={4}
                    >
                      Payrun ID
                    </Th>
                    <Th
                      border="none"
                      color={subTextColor}
                      fontSize="xs"
                      fontWeight="bold"
                      letterSpacing="wider"
                      textTransform="uppercase"
                      py={4}
                      cursor="pointer"
                      onClick={() => handleSort("payout_count")}
                      _hover={{ color: "brand.500" }}
                    >
                      <HStack spacing={1}>
                        <Text>Payouts</Text>
                        <Icon as={getSortIcon("payout_count")} boxSize={3} />
                      </HStack>
                    </Th>
                    <Th
                      border="none"
                      color={subTextColor}
                      fontSize="xs"
                      fontWeight="bold"
                      letterSpacing="wider"
                      textTransform="uppercase"
                      py={4}
                      cursor="pointer"
                      onClick={() => handleSort("total_gross_amount")}
                      _hover={{ color: "brand.500" }}
                    >
                      <HStack spacing={1}>
                        <Text>Total Gross</Text>
                        <Icon
                          as={getSortIcon("total_gross_amount")}
                          boxSize={3}
                        />
                      </HStack>
                    </Th>
                    <Th
                      border="none"
                      color={subTextColor}
                      fontSize="xs"
                      fontWeight="bold"
                      letterSpacing="wider"
                      textTransform="uppercase"
                      py={4}
                      cursor="pointer"
                      onClick={() => handleSort("total_net_amount")}
                      _hover={{ color: "brand.500" }}
                    >
                      <HStack spacing={1}>
                        <Text>Total Net</Text>
                        <Icon
                          as={getSortIcon("total_net_amount")}
                          boxSize={3}
                        />
                      </HStack>
                    </Th>
                    <Th
                      border="none"
                      color={subTextColor}
                      fontSize="xs"
                      fontWeight="bold"
                      letterSpacing="wider"
                      textTransform="uppercase"
                      py={4}
                      textAlign="center"
                    >
                      Actions
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {paginatedPayruns.map((payrun, index) => (
                    <MotionTr
                      key={payrun.payrun_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, delay: index * 0.02 }}
                      _hover={{ bg: hoverBg }}
                      cursor="pointer"
                    >
                      <Td border="none" py={4}>
                        <HStack spacing={3}>
                          <Box
                            w="40px"
                            h="40px"
                            borderRadius="lg"
                            bg="brand.500"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            color="white"
                          >
                            <Icon as={FaCalendarAlt} boxSize={5} />
                          </Box>
                          <Text
                            fontSize="sm"
                            fontWeight="600"
                            color={textColor}
                          >
                            {payrun.month} {payrun.year}
                          </Text>
                        </HStack>
                      </Td>
                      <Td border="none" py={4}>
                        <Badge
                          colorScheme="purple"
                          variant="subtle"
                          borderRadius="full"
                          px={3}
                          py={1}
                          fontSize="sm"
                        >
                          {payrun.payrun_id}
                        </Badge>
                      </Td>
                      <Td border="none" py={4}>
                        <HStack spacing={2}>
                          <Icon as={FaUsers} color="blue.500" boxSize={4} />
                          <Text
                            fontSize="sm"
                            fontWeight="600"
                            color={textColor}
                          >
                            {payrun.payout_count}
                          </Text>
                        </HStack>
                      </Td>
                      <Td border="none" py={4}>
                        <Text fontSize="sm" fontWeight="600" color={textColor}>
                          ₹
                          {payrun.total_gross_amount.toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                          })}
                        </Text>
                      </Td>
                      <Td border="none" py={4}>
                        <Text fontSize="sm" fontWeight="600" color="green.500">
                          ₹
                          {payrun.total_net_amount.toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                          })}
                        </Text>
                      </Td>
                      <Td border="none" py={4}>
                        <Flex justify="center">
                          <Tooltip label="View Payouts" placement="top">
                            <IconButton
                              size="sm"
                              variant="ghost"
                              colorScheme="brand"
                              icon={<Icon as={FaEye} />}
                              borderRadius="lg"
                              onClick={() => handleViewPayrun(payrun)}
                              _hover={{
                                bg: "rgba(35, 169, 121, 0.1)",
                                transform: "scale(1.1)",
                              }}
                              transition="all 0.2s"
                            />
                          </Tooltip>
                        </Flex>
                      </Td>
                    </MotionTr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Flex
              justify="space-between"
              align="center"
              mt={4}
              direction={{ base: "column", md: "row" }}
              gap={4}
            >
              <HStack spacing={2}>
                <Text fontSize="sm" color={subTextColor}>
                  Show
                </Text>
                <select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  style={{
                    padding: "4px 8px",
                    borderRadius: "8px",
                    border: `1px solid ${borderColor}`,
                    fontSize: "14px",
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <Text fontSize="sm" color={subTextColor}>
                  of {payrunsWithTotals.length} entries
                </Text>
              </HStack>

              <HStack spacing={2}>
                <IconButton
                  size="sm"
                  icon={<Icon as={FaChevronLeft} />}
                  onClick={() => handlePageChange(currentPage - 1)}
                  isDisabled={currentPage === 1}
                  variant="ghost"
                  borderRadius="lg"
                />
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      size="sm"
                      onClick={() => handlePageChange(pageNum)}
                      variant={currentPage === pageNum ? "solid" : "ghost"}
                      colorScheme={currentPage === pageNum ? "brand" : "gray"}
                      borderRadius="lg"
                      minW="40px"
                    >
                      {pageNum}
                    </Button>
                  );
                })}
                <IconButton
                  size="sm"
                  icon={<Icon as={FaChevronRight} />}
                  onClick={() => handlePageChange(currentPage + 1)}
                  isDisabled={currentPage === totalPages}
                  variant="ghost"
                  borderRadius="lg"
                />
              </HStack>
            </Flex>
          </>
        ) : (
          <VStack spacing={4} py={12}>
            <Icon
              as={FaFileAlt}
              boxSize={16}
              color={subTextColor}
              opacity={0.5}
            />
            <Text fontSize="xl" fontWeight="bold" color={textColor}>
              No payruns found
            </Text>
            <Text fontSize="sm" color={subTextColor}>
              {selectedMonth !== "all" || selectedYear !== "all"
                ? "Try adjusting your filters"
                : "No payrun data available"}
            </Text>
          </VStack>
        )}
      </VStack>
    </GlassMorphismCard>
  );
}
